/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/observability.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


/**
 * Only `schema` is evaluated today. The other three accept configuration and produce no
 * observations until their adapter lands; see the top of this document.
 */
export type MonitorKind = 'schema' | 'volume' | 'freshness' | 'assessment';

export const MONITOR_KINDS: readonly MonitorKind[] = ['schema', 'volume', 'freshness', 'assessment'];

export function isMonitorKind(value: unknown): value is MonitorKind {
  return (['schema', 'volume', 'freshness', 'assessment'] as readonly unknown[]).includes(value);
}

/**
 * Wider than `MonitorKind`, because not every incident comes from a monitor. `contract_breach` is
 * enumerated before `core.contracts` exists so that module can publish an event this one already
 * consumes — and because a `kind` CHECK is cheaper to get right before there are rows.
 * `rule_persistent_failure` is raised by the automated detection engine when one rule binding
 * keeps finding violations across its recent successful executions. Read `subject_kind` before
 * dereferencing `subject_id` on one: it is a **rule binding**, not a dataset.
 * `reconciliation_break` is raised when a reconciliation run finds the two sides disagreeing. Its
 * subject is the **definition**, not the run, so a nightly reconciliation against a source that
 * stays broken is one incident that remains open rather than one per night. The run is on
 * `evidence_ref`. A run that ends `keys_disjoint` or `keys_not_unique` raises nothing: those say
 * the definition is misconfigured, which is a problem for whoever wrote it rather than a finding
 * about the customer's data. The three `*_schedule_failing` kinds mean a tenant's *scheduled* work
 * has stopped running — a nightly harvest, a rule execution, a reconciliation run. Three kinds
 * rather than one because each carries a different subject, and because
 * `NotificationRule.incident_kind` filters on this: a tenant can send harvest failures to whoever
 * owns the source credentials and rule failures somewhere else. They are raised from two different
 * places, which is worth knowing before reading one. A schedule that could not **dispatch** — over
 * quota, work already running, residency — carries no `evidence_ref`. A schedule that dispatched
 * and whose **work then failed at the source** — a rejected credential, an unreachable host —
 * carries the operation that failed. `cause` names which of ten classified reasons it was, and the
 * alert's wording is selected from it.
 */
export type IncidentKind = 'schema_drift' | 'freshness_breach' | 'volume_anomaly' | 'assessment_overdue' | 'contract_breach' | 'rule_persistent_failure' | 'reconciliation_break' | 'harvest_schedule_failing' | 'execution_schedule_failing' | 'reconciliation_schedule_failing';

export const INCIDENT_KINDS: readonly IncidentKind[] = ['schema_drift', 'freshness_breach', 'volume_anomaly', 'assessment_overdue', 'contract_breach', 'rule_persistent_failure', 'reconciliation_break', 'harvest_schedule_failing', 'execution_schedule_failing', 'reconciliation_schedule_failing'];

export function isIncidentKind(value: unknown): value is IncidentKind {
  return (['schema_drift', 'freshness_breach', 'volume_anomaly', 'assessment_overdue', 'contract_breach', 'rule_persistent_failure', 'reconciliation_break', 'harvest_schedule_failing', 'execution_schedule_failing', 'reconciliation_schedule_failing'] as readonly unknown[]).includes(value);
}

/**
 * What `Incident.subject_id` points at. Read it rather than inferring from `kind`. The four
 * monitor kinds carry a dataset and `contract_breach` carries a contract, which prose used to be
 * enough to say; a third referent arrived with `rule_persistent_failure` and the pair is typed
 * now, so a client asks instead of assuming. Dereferencing a binding id as a dataset renders a raw
 * UUID at the one place a human reads it. `reconciliation_definition` is the fourth. `connection`
 * is the fifth, carried by `harvest_schedule_failing`. The connection rather than the schedule
 * row: a schedule is configuration *about* a source, and two schedules on one source that stop for
 * the same reason are one problem rather than two.
 */
export type SubjectKind = 'dataset' | 'contract' | 'rule_binding' | 'reconciliation_definition' | 'connection';

export const SUBJECT_KINDS: readonly SubjectKind[] = ['dataset', 'contract', 'rule_binding', 'reconciliation_definition', 'connection'];

export function isSubjectKind(value: unknown): value is SubjectKind {
  return (['dataset', 'contract', 'rule_binding', 'reconciliation_definition', 'connection'] as readonly unknown[]).includes(value);
}

/**
 * What `Incident.evidence_ref` points at, for the same reason `SubjectKind` exists one field over:
 * an observation for the monitor kinds, the dataset for `contract_breach`, the run for
 * `reconciliation_break`. `null` exactly when `evidence_ref` is null, which is an ordinary state
 * rather than a gap — a staleness incident about a dataset that was never observed has no
 * observation to cite, and `rule_persistent_failure` never has one at all because its evidence is
 * a count over tasks rather than a row. `harvest_operation` and `rule_execution` are carried by
 * the `*_schedule_failing` kinds when the incident came from dispatched work failing, and are null
 * when it came from a schedule that could not dispatch at all — the same "null together, or agree"
 * rule, reached by kinds that have two openers rather than one.
 */
export type EvidenceKind = 'observation' | 'dataset' | 'reconciliation_run' | 'harvest_operation' | 'rule_execution';

export const EVIDENCE_KINDS: readonly EvidenceKind[] = ['observation', 'dataset', 'reconciliation_run', 'harvest_operation', 'rule_execution'];

export function isEvidenceKind(value: unknown): value is EvidenceKind {
  return (['observation', 'dataset', 'reconciliation_run', 'harvest_operation', 'rule_execution'] as readonly unknown[]).includes(value);
}

/**
 * A property of the finding, never of how it will be delivered. It is natural to call something
 * critical *because* somebody asked to be paged about it; routing is a decision made afterwards
 * from the severity, not the other way round.
 */
export type Severity = 'info' | 'warning' | 'critical';

export const SEVERITYS: readonly Severity[] = ['info', 'warning', 'critical'];

export function isSeverity(value: unknown): value is Severity {
  return (['info', 'warning', 'critical'] as readonly unknown[]).includes(value);
}

/** `acknowledged` means seen, not fixed — it is still active. There is no `deleted`. */
export type IncidentState = 'open' | 'acknowledged' | 'resolved';

export const INCIDENT_STATES: readonly IncidentState[] = ['open', 'acknowledged', 'resolved'];

export function isIncidentState(value: unknown): value is IncidentState {
  return (['open', 'acknowledged', 'resolved'] as readonly unknown[]).includes(value);
}

export interface Incident {
  readonly id: string;
  readonly kind: IncidentKind;
  /**
   * What this incident is about. **Read `subject_kind` to know what it is** — a dataset for the
   * monitor kinds, a contract for `contract_breach`, a rule binding for `rule_persistent_failure`.
   * A bare id rather than a discriminated union, because typing it would make this module depend
   * on every module that can raise an incident — the coupling the one-way event seam exists to
   * avoid.
   */
  readonly subject_id: string;
  readonly subject_kind: SubjectKind;
  readonly severity: Severity;
  readonly state: IncidentState;
  /**
   * A stable machine-readable reason code — `schema.column_dropped`,
   * `schema.possible_rename_unresolvable`. What a notification template is keyed on and what a
   * dashboard groups by, kept separate from the sentence because wording changes without the
   * meaning changing.
   */
  readonly cause: string;
  /**
   * One sentence a human reads, built from column **names** and counts. Never a value from the
   * dataset.
   */
  readonly summary: string;
  readonly opened_at: string;
  /** Present exactly when `state` is `resolved`. */
  readonly resolved_at: string | null;
  /**
   * Who said they were looking at this — the `sub` claim of the token that acknowledged it,
   * written in the same statement as the state change, so an acknowledgement cannot exist without
   * a name against it. `null` means nobody has, or the incident predates the column. An honest
   * absence rather than an invented actor: "the tenancy acknowledged it" is not a fact anybody can
   * act on during a review.
   */
  readonly acknowledged_by: string | null;
  /**
   * Who declared it over. Separate from `acknowledged_by` because the handover is what an incident
   * review asks about — one person picks it up, another closes it, and a single field would lose
   * whichever half is more interesting.
   */
  readonly resolved_by: string | null;
  /**
   * What opened it, by id — a reference rather than the reading, so a recomputation cannot leave
   * two copies that disagree. **Read `evidence_kind` to know what it is.**
   */
  readonly evidence_ref: string | null;
  /**
   * What `evidence_ref` points at, or `null` when there is nothing to point at. Null exactly when
   * `evidence_ref` is null: the two travel together, and a client that reads one without the other
   * is back to inferring the referent from `kind`.
   */
  readonly evidence_kind: EvidenceKind | null;
  /**
   * `state` is not `resolved`. Sent rather than left to the client, so "is this still happening"
   * is decided in one place — a client testing `state !== 'resolved'` would have to be updated by
   * every consumer the day a fourth state exists.
   */
  readonly active: boolean;
}

export function isIncident(value: unknown): value is Incident {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    (['schema_drift', 'freshness_breach', 'volume_anomaly', 'assessment_overdue', 'contract_breach', 'rule_persistent_failure', 'reconciliation_break', 'harvest_schedule_failing', 'execution_schedule_failing', 'reconciliation_schedule_failing'] as readonly unknown[]).includes(candidate['kind']) &&
    typeof candidate['subject_id'] === 'string' &&
    (['dataset', 'contract', 'rule_binding', 'reconciliation_definition', 'connection'] as readonly unknown[]).includes(candidate['subject_kind']) &&
    (['info', 'warning', 'critical'] as readonly unknown[]).includes(candidate['severity']) &&
    (['open', 'acknowledged', 'resolved'] as readonly unknown[]).includes(candidate['state']) &&
    typeof candidate['cause'] === 'string' &&
    typeof candidate['summary'] === 'string' &&
    typeof candidate['opened_at'] === 'string' &&
    typeof candidate['active'] === 'boolean' &&
    (typeof candidate['acknowledged_by'] === 'string' || candidate['acknowledged_by'] === null) &&
    (typeof candidate['resolved_by'] === 'string' || candidate['resolved_by'] === null) &&
    (typeof candidate['resolved_at'] === 'string' || candidate['resolved_at'] === null) &&
    (typeof candidate['evidence_ref'] === 'string' || candidate['evidence_ref'] === null) &&
    (candidate['evidence_kind'] === null || isEvidenceKind(candidate['evidence_kind']))
  );
}

export interface IncidentPage {
  /**
   * ADR-009. The incident feed is one of the four CQRS read models, with a 30-second budget.
   * **Null**, because the feed is served directly from `observability.incident` — which means
   * *current*, not unknown. Added in K21+: it was absent entirely, so ADR-009's "every read model
   * carries `projected_at`" was false here while being true for the other three. A client cannot
   * special-case its absence for one feed and get it right for the rest.
   */
  readonly projected_at: string | null;
  /** ADR-009 gives the incident feed 30 seconds. Null `projected_at` means current. */
  readonly staleness_budget_seconds?: number;
  readonly items: readonly Incident[];
  /**
   * How many are in `items`, not how many exist. The queue is bounded, and a total would require a
   * second count query to answer a question the screen does not ask.
   */
  readonly count: number;
}

export function isIncidentPage(value: unknown): value is IncidentPage {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['items']) &&
    (candidate['items'] as readonly unknown[]).every((item) => isIncident(item)) &&
    typeof candidate['count'] === 'number' &&
    (typeof candidate['projected_at'] === 'string' || candidate['projected_at'] === null)
  );
}

export interface Observation {
  readonly id: string;
  readonly dataset_id: string;
  readonly kind: MonitorKind;
  /**
   * When the **source** was seen, not when the platform consumed the event. Using the consumer's
   * clock would date every observation to the relay's wake-up and make a latency question
   * unanswerable from the series.
   */
  readonly observed_at: string;
  /**
   * The metric — a count of changed columns, a row estimate, an age in seconds. **`null` means the
   * measurement could not be taken, and is not a zero.**
   */
  readonly value: number | null;
  /**
   * Why the measurement is missing, when it is; empty otherwise. Present so an operator sees "no
   * timestamp column is configured" rather than a blank cell — the difference between a fixable
   * condition and an apparent bug. A delivery or measurement failure, never a data value.
   */
  readonly detail: string;
  /** `value` is not null. Same argument as `Incident.active`. */
  readonly measured: boolean;
}

export function isObservation(value: unknown): value is Observation {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['dataset_id'] === 'string' &&
    (['schema', 'volume', 'freshness', 'assessment'] as readonly unknown[]).includes(candidate['kind']) &&
    typeof candidate['observed_at'] === 'string' &&
    (typeof candidate['value'] === 'number' || candidate['value'] === null) &&
    typeof candidate['detail'] === 'string' &&
    typeof candidate['measured'] === 'boolean'
  );
}

export interface ObservationPage {
  readonly items: readonly Observation[];
  readonly count: number;
  /**
   * True when this monitor has never run on this dataset. Distinct from an empty page after
   * filtering and distinct from a `null` value: three different absences, and a client that
   * conflated them would report an unconfigured monitor as a failing one.
   */
  readonly never_observed: boolean;
}

export function isObservationPage(value: unknown): value is ObservationPage {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['items']) &&
    (candidate['items'] as readonly unknown[]).every((item) => isObservation(item)) &&
    typeof candidate['count'] === 'number' &&
    typeof candidate['never_observed'] === 'boolean'
  );
}

export interface MonitorConfig {
  readonly dataset_id: string;
  readonly kind: MonitorKind;
  /** The boundary. Units belong to the kind: seconds for freshness, a ratio for volume. */
  readonly limit: number;
  /**
   * How many observations in a row must breach before an incident opens. Above one for volume on
   * purpose — `reltuples` is maintained by `ANALYZE` and moves for reasons unrelated to the data,
   * so a single-observation threshold on it fires on an autovacuum pattern.
   */
  readonly consecutive: number;
  /** Required for `freshness`, and forbidden for every other kind. */
  readonly freshness_column?: string | null;
  /**
   * Whether this monitor kind is actually evaluated in this deployment. False for volume,
   * freshness and SLA today. Sent so a screen can say "configured, not yet evaluated" instead of
   * showing a threshold that looks live.
   */
  readonly evaluated: boolean;
}

export function isMonitorConfig(value: unknown): value is MonitorConfig {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['dataset_id'] === 'string' &&
    (['schema', 'volume', 'freshness', 'assessment'] as readonly unknown[]).includes(candidate['kind']) &&
    typeof candidate['limit'] === 'number' &&
    typeof candidate['consecutive'] === 'number' &&
    typeof candidate['evaluated'] === 'boolean'
  );
}

export interface MonitorConfigWrite {
  readonly limit: number;
  readonly consecutive?: number;
  readonly freshness_column?: string | null;
}

export function isMonitorConfigWrite(value: unknown): value is MonitorConfigWrite {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['limit'] === 'number'
  );
}

/**
 * SMTP or a webhook. No PagerDuty and no Slack SaaS: hard rule 13 requires air-gap operation, and
 * a customer's own gateway satisfies `webhook`.
 */
export type NotificationChannel = 'email' | 'webhook';

export const NOTIFICATION_CHANNELS: readonly NotificationChannel[] = ['email', 'webhook'];

export function isNotificationChannel(value: unknown): value is NotificationChannel {
  return (['email', 'webhook'] as readonly unknown[]).includes(value);
}

/**
 * `deployment_default` is the destination the operator configured for this deployment, and is
 * always deliverable if the transport exists. The other two are tenant-supplied and are bounded by
 * the deployment's own allow-list — an empty allow-list means they are unavailable, which is
 * fail-closed and is the default.
 */
export type NotificationTargetKind = 'deployment_default' | 'email_recipients' | 'webhook_url';

export const NOTIFICATION_TARGET_KINDS: readonly NotificationTargetKind[] = ['deployment_default', 'email_recipients', 'webhook_url'];

export function isNotificationTargetKind(value: unknown): value is NotificationTargetKind {
  return (['deployment_default', 'email_recipients', 'webhook_url'] as readonly unknown[]).includes(value);
}

export interface NotificationRule {
  /**
   * The nil UUID on a default rule, which is not a stored row: it has no author, cannot be
   * withdrawn, and must not be rendered as something somebody chose.
   */
  readonly id: string;
  readonly channel: NotificationChannel;
  readonly min_severity: Severity;
  /**
   * **Null means every kind**, not "unknown" and not "none". Nullable rather than an `all` member,
   * because "any" is the absence of a filter rather than a kind.
   */
  readonly incident_kind: IncidentKind | null;
  readonly target_kind: NotificationTargetKind;
  /**
   * Empty unless `target_kind` is `email_recipients`. Returned as declared, because this is the
   * tenant's own configuration and a screen that cannot show what it stored cannot let anybody
   * check it. The **audit** record carries a count instead: that entry is shipped to a SIEM, where
   * a mailbox address is personal data the record does not need to be useful.
   */
  readonly recipients: readonly string[];
  /** Null unless `target_kind` is `webhook_url`. */
  readonly webhook_url: string | null;
  /**
   * Always true today. There is no route that toggles it — silencing a channel is withdrawing the
   * rule — and the field is on the wire because the column exists and a client should not infer a
   * rule is live from the absence of a flag.
   */
  readonly enabled: boolean;
  /**
   * A host-written one-line description of where this rule sends, safe to log. Never a credential,
   * and for an email target a count rather than the addresses.
   */
  readonly destination: string;
}

export function isNotificationRule(value: unknown): value is NotificationRule {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    (['email', 'webhook'] as readonly unknown[]).includes(candidate['channel']) &&
    (['info', 'warning', 'critical'] as readonly unknown[]).includes(candidate['min_severity']) &&
    (candidate['incident_kind'] === null || isIncidentKind(candidate['incident_kind'])) &&
    (['deployment_default', 'email_recipients', 'webhook_url'] as readonly unknown[]).includes(candidate['target_kind']) &&
    Array.isArray(candidate['recipients']) &&
    (candidate['recipients'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    (typeof candidate['webhook_url'] === 'string' || candidate['webhook_url'] === null) &&
    typeof candidate['enabled'] === 'boolean' &&
    typeof candidate['destination'] === 'string'
  );
}

export interface NotificationRuleWrite {
  readonly channel: NotificationChannel;
  readonly min_severity: Severity;
  /** Omit or send null to route every kind. */
  readonly incident_kind?: IncidentKind | null;
  readonly target_kind?: NotificationTargetKind;
  /**
   * Required when `target_kind` is `email_recipients`, and forbidden otherwise. A distribution
   * list is the customer's own object past ten addresses.
   */
  readonly recipients?: readonly string[];
  /**
   * Required when `target_kind` is `webhook_url`, and forbidden otherwise. `http` or `https`, and
   * never with userinfo.
   */
  readonly webhook_url?: string;
}

export function isNotificationRuleWrite(value: unknown): value is NotificationRuleWrite {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['email', 'webhook'] as readonly unknown[]).includes(candidate['channel']) &&
    (['info', 'warning', 'critical'] as readonly unknown[]).includes(candidate['min_severity'])
  );
}

export interface NotificationRuleEnabledWrite {
  /**
   * True brings the rule back, false silences it. Required, and deliberately not defaulted: a
   * missing value is not "switch it off", because silencing a paging rule is a decision somebody
   * makes explicitly.
   */
  readonly enabled: boolean;
}

export function isNotificationRuleEnabledWrite(value: unknown): value is NotificationRuleEnabledWrite {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['enabled'] === 'boolean'
  );
}

export interface RoutingPolicy {
  /**
   * **The one mail domain this tenancy may route alert email to**, taken from the address the
   * tenancy was registered at — which signup verified by sending a code to it, so it is a domain
   * the registrant demonstrably receives mail at rather than one they typed into a form.
   * Whole-label and case-insensitive: a `bank.com` tenancy does not admit `evil-bank.com`, and a
   * subdomain such as `ops.bank.com` is a different mail domain and is not admitted either.
   * Published so a client can state the restriction *before* somebody submits an address, rather
   * than letting them discover it through a 422. It is the caller's own domain, so it discloses
   * nothing they do not already know about themselves. **`null` means this platform has no record
   * of a domain for this tenancy** — a tenancy registered before it was recorded, or one whose
   * users span several domains so the backfill had no unambiguous answer. It is not "any domain":
   * a tenant-supplied email destination is refused entirely until an operator records one, because
   * admitting anything would grant a domain nobody verified. A tenant cannot set or widen this
   * value; it is written once at registration and is outside every column-scoped `UPDATE` grant
   * the platform holds. This replaced a deployment-wide `LUMIQ_ALERT_EMAIL_DOMAINS` allow-list,
   * and the replacement is tighter: one list served every tenant, so on a pooled install two
   * allow-listed customers were both permitted for *everyone*.
   */
  readonly allowed_email_domain: string | null;
  /**
   * **False means nobody has chosen**, and the rules below are the platform's default — critical
   * incidents, by email, to the deployment's configured destination. A defaulted policy rendered
   * as a configured one reads as a decision somebody made, and "nobody has chosen" is a different
   * conversation with a customer. True with an empty `rules` array is meaningful and different
   * again: this tenant has deliberately turned notification off.
   */
  readonly declared: boolean;
  readonly rules: readonly NotificationRule[];
}

export function isRoutingPolicy(value: unknown): value is RoutingPolicy {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['declared'] === 'boolean' &&
    (typeof candidate['allowed_email_domain'] === 'string' || candidate['allowed_email_domain'] === null) &&
    Array.isArray(candidate['rules']) &&
    (candidate['rules'] as readonly unknown[]).every((item) => isNotificationRule(item))
  );
}

/**
 * One recorded attempt to deliver an alert about an incident. Carries the template **key** and
 * never the rendered text. A log of rendered alerts would move the disclosure surface into the
 * database rather than remove it; the wording lives in one reviewable catalogue on the server.
 */
export interface NotificationAttempt {
  /** Which transport this attempt used. */
  readonly channel: 'email' | 'webhook';
  /** The incident's `cause`, which selects the wording. Not the wording itself. */
  readonly template_key: string;
  /** Whether the transport accepted it. */
  readonly succeeded: boolean;
  /**
   * Why it failed, host-written, or `""` on success. Always present: absent-versus-empty is a
   * distinction a client would have to guess at. Never a value from a customer's data — the same
   * rule the whole notification path runs under. A missing transport, an SMTP rejection, a
   * destination an operator's allow-list no longer permits.
   */
  readonly detail: string;
  readonly attempted_at: string;
}

export function isNotificationAttempt(value: unknown): value is NotificationAttempt {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['email', 'webhook'] as readonly unknown[]).includes(candidate['channel']) &&
    typeof candidate['template_key'] === 'string' &&
    typeof candidate['succeeded'] === 'boolean' &&
    typeof candidate['detail'] === 'string' &&
    typeof candidate['attempted_at'] === 'string'
  );
}
